import { forwardRef } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva(
	"inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
	{
		variants: {
			variant: {
				default: "bg-muted",
				outline: "border border-border bg-transparent",
				underline: "bg-transparent border-b border-border rounded-none p-0 h-auto",
			}
		},
		defaultVariants: {
			variant: "default"
		}
	}
);

const TabsList = forwardRef(({ className, variant, ...props }, ref) => (
	<TabsPrimitive.List
		ref={ref}
		className={cn(tabsListVariants({ variant, className }))}
		{...props}
	/>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				default: "data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm",
				outline: "border border-transparent data-[state=active]:border-border data-[state=active]:bg-card data-[state=active]:shadow-sm",
				underline: "border-b-2 border-transparent rounded-none data-[state=active]:border-brand-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none pb-3",
			}
		},
		defaultVariants: {
			variant: "default"
		}
	}
);

const TabsTrigger = forwardRef(({ className, variant, ...props }, ref) => (
	<TabsPrimitive.Trigger
		ref={ref}
		className={cn(tabsTriggerVariants({ variant, className }))}
		{...props}
	/>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = forwardRef(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			"mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
			className
		)}
		{...props}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// Simple tab component for basic use cases
const SimpleTabBar = forwardRef(({
	tabs = [],
	activeTab,
	onTabChange,
	variant = "underline",
	className,
	showCounts = false,
	...props
}, ref) => (
	<Tabs value={activeTab} onValueChange={onTabChange} className={className} {...props}>
		{/* Scroll the tab row horizontally on narrow screens instead of overflowing the page */}
		<div className="overflow-x-auto">
			<TabsList variant={variant} ref={ref}>
				{tabs.map((tab) => (
					<TabsTrigger key={tab.id} value={tab.id} variant={variant} className="flex-shrink-0">
						{tab.label}
						{showCounts && tab.count !== undefined && (
							<span className="ml-2 text-xs bg-accent text-muted-foreground px-1.5 py-0.5 rounded-full">
								{tab.count}
							</span>
						)}
					</TabsTrigger>
				))}
			</TabsList>
		</div>
	</Tabs>
));
SimpleTabBar.displayName = "SimpleTabBar";

export { Tabs, TabsList, TabsTrigger, TabsContent, SimpleTabBar }; 